import { supabase } from './supabaseClient';

async function callMembershipRpc(functionName, parameters = {}) {
  const { data, error } = await supabase.rpc(functionName, parameters);

  if (error) {
    throw error;
  }

  if (data === null || data === undefined) {
    throw new Error(`${functionName} returned no membership data.`);
  }

  return data;
}

function requireArray(value, functionName) {
  if (!Array.isArray(value)) {
    throw new Error(`${functionName} returned an invalid membership list.`);
  }

  return value;
}

function requireObject(value, functionName) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${functionName} returned an invalid membership result.`);
  }

  return value;
}

async function getLegacyOrganisationMembership() {
  const {
    data: { user } = {},
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user?.id) {
    throw new Error('Authentication is required to load memberships.');
  }

  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select('auth_id, organisation_id')
    .eq('auth_id', user.id)
    .maybeSingle();

  if (profileError) {
    throw profileError;
  }

  if (!profile) {
    return null;
  }

  if (profile.auth_id !== user.id) {
    throw new Error('The authenticated profile could not be verified.');
  }

  if (!profile.organisation_id) {
    return null;
  }

  const { data: organisation, error: organisationError } = await supabase
    .from('organisations')
    .select('id, name, home_airfield')
    .eq('id', profile.organisation_id)
    .maybeSingle();

  if (organisationError) {
    throw organisationError;
  }

  if (!organisation || organisation.id !== profile.organisation_id) {
    throw new Error('The organisation linked to your profile could not be loaded.');
  }

  return {
    membership_id: `legacy-${organisation.id}`,
    organisation_id: organisation.id,
    organisation_name: organisation.name,
    location: organisation.home_airfield,
    status: 'current',
    requested_at: null,
    source: 'legacy',
  };
}

export async function listMembershipOrganisations() {
  const functionName = 'list_membership_organisations';
  return requireArray(
    await callMembershipRpc(functionName),
    functionName
  );
}

export async function listMyOrganisationMemberships() {
  const functionName = 'list_my_organisation_memberships';
  const [membershipRows, legacyMembership] = await Promise.all([
    callMembershipRpc(functionName).then((rows) => requireArray(rows, functionName)),
    getLegacyOrganisationMembership(),
  ]);

  if (!legacyMembership) {
    return membershipRows;
  }

  const legacyOrganisationId = String(legacyMembership.organisation_id);
  return [
    legacyMembership,
    ...membershipRows.filter(
      (membership) => String(membership.organisation_id) !== legacyOrganisationId
    ),
  ];
}

export async function listSelectableOrganisations() {
  const memberships = await listMyOrganisationMemberships();
  const seenOrganisationIds = new Set();

  return memberships.filter((membership) => {
    const isLegacyMembership =
      membership.source === 'legacy' && membership.status === 'current';
    const isApprovedMembership = membership.status === 'active';
    const organisationId = String(membership.organisation_id || '');

    if ((!isLegacyMembership && !isApprovedMembership) || !organisationId) {
      return false;
    }

    if (seenOrganisationIds.has(organisationId)) {
      return false;
    }

    seenOrganisationIds.add(organisationId);
    return true;
  });
}

export async function requestOrganisationMembership(organisationId) {
  const functionName = 'request_organisation_membership';
  return requireObject(
    await callMembershipRpc(functionName, {
      p_organisation_id: organisationId,
    }),
    functionName
  );
}

export async function listAdminOrganisations() {
  const functionName = 'list_membership_admin_organisations';
  return requireArray(
    await callMembershipRpc(functionName),
    functionName
  );
}

export async function listOrganisationMembershipRequests(
  organisationId,
  status = 'pending'
) {
  const functionName = 'list_organisation_membership_requests';
  return requireArray(
    await callMembershipRpc(functionName, {
      p_organisation_id: organisationId,
      p_status: status,
    }),
    functionName
  );
}

export async function reviewOrganisationMembershipRequest(
  membershipId,
  decision
) {
  if (!['approve', 'reject'].includes(decision)) {
    throw new Error('Membership decision must be approve or reject.');
  }

  const functionName = 'review_organisation_membership_request';
  return requireObject(
    await callMembershipRpc(functionName, {
      p_membership_id: membershipId,
      p_decision: decision,
    }),
    functionName
  );
}
